import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Chip,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  MoreVert as MoreVertIcon,
  DriveFileMoveOutlined as DriveFileMoveIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatDate, getStatusKey, getStatusStyle } from '../../../utils/utils';

const COLUMNS = [
  { id: 'title', labelKey: 'repository.table.title', sortable: true, minWidth: 200 },
  { id: 'status', labelKey: 'repository.table.status', sortable: true, minWidth: 120 },
  { id: 'type', labelKey: 'repository.table.type', sortable: true, minWidth: 120 },
  { id: 'collaborators', labelKey: 'repository.table.parties', sortable: false, minWidth: 150 },
  { id: 'updated_at', labelKey: 'repository.table.updated', sortable: true, minWidth: 130 },
  { id: 'created_at', labelKey: 'repository.table.created', sortable: true, minWidth: 130 },
];

const ContractTable = ({
  filteredContracts,
  onContractClick,
  onContractDoubleClick,
  selectedContractId,
  selectedIds = [],
  onSelectionChange,
  handleMoveContract,
}) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isDark = theme.palette.mode === 'dark';
  const goldAccent = '#c9a96e';

  const [orderBy, setOrderBy] = useState('updated_at');
  const [order, setOrder] = useState('desc');
  const [menuState, setMenuState] = useState({ anchor: null, contract: null });
  const closeMenu = () => setMenuState({ anchor: null, contract: null });

  const selectionEnabled = typeof onSelectionChange === 'function';

  const handleSort = (columnId) => {
    if (orderBy === columnId) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(columnId);
      setOrder('asc');
    }
  };

  const sortedContracts = React.useMemo(() => {
    if (!filteredContracts) return [];
    const sorted = [...filteredContracts];

    sorted.sort((a, b) => {
      let aVal, bVal;

      switch (orderBy) {
        case 'title':
          aVal = (a.title || '').toLowerCase();
          bVal = (b.title || '').toLowerCase();
          return order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          return order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        case 'type':
          aVal = a.type || '';
          bVal = b.type || '';
          return order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        case 'updated_at':
          aVal = new Date(a.updated_at || 0);
          bVal = new Date(b.updated_at || 0);
          return order === 'asc' ? aVal - bVal : bVal - aVal;
        case 'created_at':
          aVal = new Date(a.created_at || 0);
          bVal = new Date(b.created_at || 0);
          return order === 'asc' ? aVal - bVal : bVal - aVal;
        default:
          return 0;
      }
    });

    return sorted;
  }, [filteredContracts, orderBy, order]);

  const allSelected =
    selectionEnabled && sortedContracts.length > 0 && sortedContracts.every((c) => selectedIds.includes(c.id));
  const someSelected =
    selectionEnabled && !allSelected && sortedContracts.some((c) => selectedIds.includes(c.id));

  const toggleAll = () => {
    if (!selectionEnabled) return;
    if (allSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange(sortedContracts.map((c) => c.id));
    }
  };

  const toggleOne = (id) => {
    if (!selectionEnabled) return;
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((x) => x !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  };

  return (
    <TableContainer
      sx={{
        borderRadius: '8px',
        border: `1px solid ${theme.palette.divider}`,
        overflow: 'auto',
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {selectionEnabled && (
              <TableCell
                padding="checkbox"
                sx={{
                  backgroundColor: isDark ? alpha('#1a1a1a', 0.95) : alpha(theme.palette.background.default, 0.95),
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  width: 40,
                }}
              >
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={toggleAll}
                  sx={{
                    color: theme.palette.text.secondary,
                    '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: goldAccent },
                  }}
                />
              </TableCell>
            )}
            {COLUMNS.map((col) => (
              <TableCell
                key={col.id}
                sx={{
                  backgroundColor: isDark ? alpha('#1a1a1a', 0.95) : alpha(theme.palette.background.default, 0.95),
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: theme.palette.text.secondary,
                  minWidth: col.minWidth,
                  whiteSpace: 'nowrap',
                  py: 1.25,
                }}
              >
                {col.sortable ? (
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => handleSort(col.id)}
                    sx={{
                      color: `${theme.palette.text.secondary} !important`,
                      '&.Mui-active': {
                        color: `${goldAccent} !important`,
                      },
                      '& .MuiTableSortLabel-icon': {
                        color: `${goldAccent} !important`,
                      },
                    }}
                  >
                    {t(col.labelKey, col.id)}
                  </TableSortLabel>
                ) : (
                  t(col.labelKey, col.id)
                )}
              </TableCell>
            ))}
            <TableCell
              sx={{
                backgroundColor: isDark ? alpha('#1a1a1a', 0.95) : alpha(theme.palette.background.default, 0.95),
                borderBottom: `1px solid ${theme.palette.divider}`,
                width: 48,
              }}
            />
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedContracts.map((contract) => {
            const statusKey = getStatusKey(contract.status);
            const statusText = t(`contractStatus.${statusKey}`);
            const isSelected = selectedContractId === contract.id;
            const isChecked = selectionEnabled && selectedIds.includes(contract.id);

            const collaboratorNames = contract.collaborators
              ? contract.collaborators.map((c) => `${c.first_name} ${c.last_name}`).join(', ')
              : '';

            return (
              <TableRow
                key={contract.id}
                hover
                draggable
                onDragStart={(e) => {
                  // If drag started on a checked row, drag all checked rows
                  const ids = isChecked && selectedIds.length > 0 ? selectedIds : [contract.id];
                  e.dataTransfer.setData('application/contract-id', String(contract.id));
                  e.dataTransfer.setData('application/contract-ids', JSON.stringify(ids));
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onClick={() => onContractClick(contract)}
                onDoubleClick={() => onContractDoubleClick(contract.id)}
                onContextMenu={(e) => {
                  if (!handleMoveContract) return;
                  e.preventDefault();
                  setMenuState({ anchor: { left: e.clientX, top: e.clientY }, contract });
                }}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: isSelected ? alpha(goldAccent, 0.08) : 'transparent',
                  '&:hover': {
                    backgroundColor: isSelected
                      ? alpha(goldAccent, 0.12)
                      : alpha(theme.palette.action.hover, 0.04),
                  },
                  '& td': {
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  },
                }}
              >
                {selectionEnabled && (
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      size="small"
                      checked={isChecked}
                      onChange={() => toggleOne(contract.id)}
                      sx={{
                        color: theme.palette.text.secondary,
                        '&.Mui-checked': { color: goldAccent },
                      }}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <Typography
                    sx={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: 300,
                    }}
                  >
                    {contract.title}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={statusText}
                    size="small"
                    sx={{
                      ...getStatusStyle(statusKey, theme),
                      height: 22,
                      fontSize: '0.7rem',
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography
                    sx={{
                      fontSize: '0.82rem',
                      color: theme.palette.text.secondary,
                    }}
                  >
                    {contract.type ? t(`contractCreationType.${contract.type}`, contract.type) : '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    sx={{
                      fontSize: '0.82rem',
                      color: theme.palette.text.secondary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: 200,
                    }}
                  >
                    {collaboratorNames || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: '0.82rem', color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}>
                    {formatDate(contract.updated_at, i18n.language)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: '0.82rem', color: theme.palette.text.secondary, whiteSpace: 'nowrap' }}>
                    {formatDate(contract.created_at, i18n.language)}
                  </Typography>
                </TableCell>
                <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                  <IconButton
                    size="small"
                    onClick={(e) => setMenuState({ anchor: e.currentTarget, contract })}
                    sx={{ color: theme.palette.text.secondary }}
                    aria-label={t('more_actions', 'More actions')}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Menu
        open={Boolean(menuState.anchor)}
        onClose={closeMenu}
        anchorEl={menuState.anchor && menuState.anchor.left == null ? menuState.anchor : null}
        anchorReference={
          menuState.anchor && menuState.anchor.left != null ? 'anchorPosition' : 'anchorEl'
        }
        anchorPosition={
          menuState.anchor && menuState.anchor.left != null ? menuState.anchor : undefined
        }
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem
          onClick={() => {
            const c = menuState.contract;
            closeMenu();
            handleMoveContract && handleMoveContract(c);
          }}
        >
          <ListItemIcon>
            <DriveFileMoveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('moveContract.menuItem', 'Move to…')} />
        </MenuItem>
      </Menu>
    </TableContainer>
  );
};

export default ContractTable;
